import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Link, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import SendIcon from '@mui/icons-material/Send';
import QuestionInput from './QuestionInput';

const sampleQuestions = [
  'When will I get married?',
  'What is my true personality?',
  'When am I likely to get promoted?',
  'How will this month go for me?',
  'What kind of year can I expect overall?',
  'Will I find lasting love soon?',
  'Is this a good time to change careers?',
  'How can I strengthen my family relationships?',
  'Is travel favored for me in the coming months?',
  'What talents or hidden strengths should I develop?',
  'When will I feel more confident and secure?',
  'What life purpose does my chart suggest?',
  'Will I be successful if I start my own business?',
  'How can I attract more abundance into my life?',
  'When is the right time to buy a house?',
];

/**
 * Shared input area used by both LandingPage and ChatPage.
 *
 * Props:
 *   - question, onChange, onClear — input state
 *   - onSubmit — called on Enter key or send button click
 *   - disabled — disables the input field
 *   - sendDisabled — disables the send button (defaults to !question)
 *   - showSuggestions — show rotating sample questions
 *   - creditsInfo — optional ReactNode rendered above input (credits/session row)
 */
const ChatInputArea = ({
  question,
  onChange,
  onClear,
  onSubmit,
  disabled = false,
  sendDisabled,
  showSuggestions = false,
  creditsInfo,
  placeholder,
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [activeSuggestion, setActiveSuggestion] = useState(0);

  const randomSuggestions = useMemo(
    () => [...sampleQuestions].sort(() => Math.random() - 0.5).slice(0, 5),
    [],
  );

  useEffect(() => {
    if (!showSuggestions) return;
    const interval = setInterval(() => {
      setActiveSuggestion((prev) => (prev + 1) % randomSuggestions.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [showSuggestions, randomSuggestions.length]);

  const isSendDisabled = sendDisabled ?? !question?.length;

  return (
    <Stack
      spacing={isMdUp ? 1 : 0.5}
      alignItems="center"
    >
      {showSuggestions && (
        <Box
          onClick={() => onChange(randomSuggestions[activeSuggestion])}
          sx={{
            cursor: 'pointer',
            color: 'text.secondary',
            fontStyle: 'italic',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            '&:hover': { color: 'primary.main' },
            minHeight: isMdUp ? 24 : 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant={isMdUp ? 'body2' : 'caption'}>
            "{randomSuggestions[activeSuggestion]}"
          </Typography>
        </Box>
      )}

      {creditsInfo}

      <Box sx={{ width: '100%', maxWidth: isMdUp ? 720 : '100%', mx: 'auto' }}>
        <QuestionInput
          value={question}
          onChange={onChange}
          onClear={onClear}
          onSubmit={onSubmit}
          disabled={disabled}
          placeholder={placeholder}
          endAdornment={
            <IconButton
              size="small"
              type="submit"
              disabled={isSendDisabled}
              onClick={(e) => { e.preventDefault(); onSubmit?.(); }}
              sx={{
                bgcolor: !isSendDisabled ? 'primary.main' : 'action.disabledBackground',
                color: !isSendDisabled ? 'white' : 'action.disabled',
                '&:hover': { bgcolor: 'primary.dark' },
                width: 32,
                height: 32,
              }}
            >
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          }
        />
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textAlign: 'center', fontSize: isMdUp ? undefined : '0.6rem' }}
      >
        <Link component={RouterLink} to="/help" color="inherit" sx={{ textDecoration: 'underline' }}>
          Help
        </Link>
        {' | '}
        <Link component={RouterLink} to="/privacy" color="inherit" sx={{ textDecoration: 'underline' }}>
          Privacy
        </Link>
        {' | '}
        <Link component={RouterLink} to="/terms" color="inherit" sx={{ textDecoration: 'underline' }}>
          Terms
        </Link>
        {' | '}
        &copy; 2026 AI Witches LLC
      </Typography>
    </Stack>
  );
};

export default ChatInputArea;
